import type { NpMessageBlockTarget } from '#graphql/build-get-messages-variables';

export type MessageBlockTarget = NpMessageBlockTarget;

export interface MessageConfig {
  target: MessageBlockTarget;
  messageId?: string;
}

export interface MessageEditValues {
  target: MessageBlockTarget;
  messageId: string;
}

export interface MessageEditRequest {
  mode: 'edit' | 'insert';
  initialValues: MessageEditValues;
  onCommit: (values: MessageEditValues) => void | Promise<void>;
}

const MESSAGE_TARGETS = new Set<MessageBlockTarget>([
  'all',
  'raw',
  'document',
  'message',
]);

const TARGET_LABELS: Record<MessageBlockTarget, string> = {
  all: 'Chat',
  raw: 'All',
  document: 'document',
  message: 'message',
};

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeMessageTarget(value: unknown): MessageBlockTarget {
  return MESSAGE_TARGETS.has(value as MessageBlockTarget)
    ? (value as MessageBlockTarget)
    : 'document';
}

export function normalizeMessageConfig(input: unknown): MessageConfig {
  const rec =
    input && typeof input === 'object'
      ? (input as Record<string, unknown>)
      : {};

  const target = normalizeMessageTarget(rec.target);
  const messageId = normalizeString(rec.messageId);

  if (target === 'message' && messageId) {
    return { target, messageId };
  }

  return { target };
}

export function stringifyMessageConfig(config: MessageConfig): string {
  const normalized = normalizeMessageConfig(config);
  if (normalized.target === 'message' && normalized.messageId) {
    return JSON.stringify(
      {
        target: 'message',
        messageId: normalized.messageId,
      },
      null,
      2,
    );
  }

  return JSON.stringify({ target: normalized.target }, null, 2);
}

export function createDefaultMessageEditValues(): MessageEditValues {
  return {
    target: 'document',
    messageId: '',
  };
}

export function messageConfigToEditValues(
  config: MessageConfig,
): MessageEditValues {
  const normalized = normalizeMessageConfig(config);
  return {
    target: normalized.target,
    messageId: normalized.messageId ?? '',
  };
}

export function messageEditValuesToConfig(
  values: MessageEditValues,
): MessageConfig {
  const target = normalizeMessageTarget(values.target);
  const messageId = normalizeString(values.messageId);
  if (target === 'message' && messageId) {
    return { target, messageId };
  }

  return { target };
}

export function getMessageValidationMessage(
  config: MessageConfig,
): string | null {
  const normalized = normalizeMessageConfig(config);
  if (normalized.target === 'message' && !normalized.messageId?.trim()) {
    return 'Message ID is required when target is a single message thread.';
  }

  return null;
}

export function describeMessageTarget(target: MessageBlockTarget): string {
  return TARGET_LABELS[target] ?? target;
}

export function describeMessage(config: MessageConfig): string {
  const normalized = normalizeMessageConfig(config);
  const label = describeMessageTarget(normalized.target);
  if (normalized.target === 'message' && normalized.messageId) {
    return `Messages · ${label} (${normalized.messageId})`;
  }

  return `Messages · ${label}`;
}

/** @deprecated Use `MessageBlockTarget` */
export type NpMessageTarget = MessageBlockTarget;
